/**
 * Space Invader co-op online: snapshot interpolation and key mapping.
 * The gateway (general_services/si_coop_rooms.py) runs the shared fleet,
 * lives and team score; this client only sends its own ship's input.
 */
import {
  RoomSocket, beaconLeaveRoom, roomInviteLink, roomParamFromSearch, roomsApi, sampleBuffer, seatStore,
} from './onlineRoomsNet';

export const SI_GAME = 'si';
export const SI_ROOM_PARAM = 'si_room';
/** Render ≈2 snapshots behind (server sends 20 Hz). */
export const SI_INTERP_DELAY_MS = 100;
/** While Space is held, re-send "fire" this often (server caps 3 bullets per ship). */
export const SI_FIRE_REPEAT_MS = 180;
export const SHIP_COLOR = { player_1: '#00ff66', player_2: '#33ccff' };
export const SHIP_LABEL = { player_1: 'Green (P1)', player_2: 'Blue (P2)' };

export const siRooms = roomsApi(SI_GAME);
const store = seatStore(SI_GAME);
export const saveSiSeat = (g) => store.save(g);
export const loadSiSeat = () => store.load();
export const clearSiSeat = () => store.clear();
export const beaconLeaveSi = (roomId, token) => beaconLeaveRoom(SI_GAME, roomId, token);
export const siRoomIdFromSearch = (search) => roomParamFromSearch(search, SI_ROOM_PARAM);
export const siInviteLink = (roomId, loc) => roomInviteLink(roomId, SI_ROOM_PARAM, loc);

export class SiRoomSocket extends RoomSocket {
  constructor(grant, opts = {}) {
    super(grant, { ...opts, game: SI_GAME });
  }
}

const lerp = (x, y, f) => x + (y - x) * f;

/**
 * Blend two co-op snapshots: ship x always; invaders only when the fleet is
 * the same set (no kill / new wave in between, and no wrap from a step down);
 * bullets and HUD numbers are taken from the newer snapshot.
 */
export function lerpSiSnapshot(a, b, f) {
  if (!a || !b) return b || a || null;
  const ships = {};
  Object.keys(b.ships || {}).forEach((sid) => {
    const sb = b.ships[sid];
    const sa = (a.ships || {})[sid];
    ships[sid] = sa ? { ...sb, x: lerp(Number(sa.x), Number(sb.x), f) } : sb;
  });
  const ia = a.invaders || [];
  const ib = b.invaders || [];
  let invaders = ib;
  if (ia.length === ib.length && ia.length && Math.abs(Number(ia[0].y) - Number(ib[0].y)) < 1) {
    invaders = ib.map((inv, i) => ({ ...inv, x: lerp(Number(ia[i].x), Number(inv.x), f) }));
  }
  return { ...b, ships, invaders };
}

export const sampleSiSnapshot = (buf, renderAt) => sampleBuffer(buf, renderAt, lerpSiSnapshot);

/** Held keys → this ship's move (fire is sent separately). */
export function siHeldMove(keys) {
  const left = keys.has('ArrowLeft') || keys.has('a') || keys.has('A');
  const right = keys.has('ArrowRight') || keys.has('d') || keys.has('D');
  if (left && !right) return 'left';
  if (right && !left) return 'right';
  return 'stay';
}

/** Scene ships for drawSpaceInvaderScene; invulnerable ships blink. */
export function sceneShips(snap, frame = 0) {
  const ships = (snap && snap.ships) || {};
  return Object.keys(ships).map((sid) => ({
    ...ships[sid],
    color: SHIP_COLOR[sid] || '#00ff00',
    blink: ships[sid].invulnerable > 0 && Math.floor(frame / 6) % 2 === 1,
  }));
}
